/* ==========================================================================
   Vitelli Moda — 2026 redesign foundation behaviour (theme 183186784579)
   Source of truth: Redesign-2026/build/foundation/assets/vm-2026.js (deployed by build/deploy-foundation.cjs)
   Loaded once from layout/theme.liquid (defer). Styles: assets/vm-2026.css §6 and §9.

   <vm-row>   horizontal product row. Reads .vm-row__track, .vm-row__arrow--prev/--next.
              Writes on itself: [data-overflow] when the track can scroll, --vm-row-pos (0..1 = scroll
              position) and --vm-row-thumb (0..1 = visible share) for the 2px progress line; disables an
              arrow at its end. Arrows page by the number of whole cards in view.
   <vm-tabs>  tabs over server-rendered panels: [role=tab] buttons with aria-controls -> panel ids.
              Click, Left/Right/Home/End; keeps aria-selected, .is-active, tabindex and panel[hidden]
              in sync; fires a bubbling `vm:tab-change` event ({detail:{tab}}).

   No dependencies; each element is defined once even if this file is included twice.
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  if (!window.customElements.get('vm-row')) {
    class VmRow extends HTMLElement {
      connectedCallback() {
        if (this._vmReady) return; // connectedCallback runs again when the row is moved in the DOM
        this.track = this.querySelector('.vm-row__track');
        if (!this.track) return;
        this._vmReady = true;
        this.prev = this.querySelector('.vm-row__arrow--prev');
        this.next = this.querySelector('.vm-row__arrow--next');
        this._frame = 0;
        this._schedule = () => {
          if (this._frame) return;
          this._frame = requestAnimationFrame(() => {
            this._frame = 0;
            this.paint();
          });
        };
        this.track.addEventListener('scroll', this._schedule, { passive: true });
        if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
        if (this.next) this.next.addEventListener('click', () => this.page(1));
        if ('ResizeObserver' in window) {
          // also fires when a hidden tab panel holding this row becomes visible
          this._observer = new ResizeObserver(this._schedule);
          this._observer.observe(this.track);
        } else {
          window.addEventListener('resize', this._schedule);
        }
        this.paint();
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
        window.removeEventListener('resize', this._schedule);
        if (this.track) this.track.removeEventListener('scroll', this._schedule);
        this._vmReady = false;
      }

      page(direction) {
        var track = this.track;
        var item = track.querySelector('.vm-row__item');
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var step = item ? item.getBoundingClientRect().width + gap : track.clientWidth;
        var perView = Math.max(1, Math.floor((track.clientWidth + gap) / step));
        track.scrollBy({ left: direction * perView * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }

      paint() {
        var track = this.track;
        var max = track.scrollWidth - track.clientWidth;
        var overflow = max > 2;
        this.toggleAttribute('data-overflow', overflow);
        var pos = overflow ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
        var thumb = track.scrollWidth > 0 ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
        this.style.setProperty('--vm-row-pos', pos.toFixed(4));
        this.style.setProperty('--vm-row-thumb', thumb.toFixed(4));
        if (this.prev) this.prev.disabled = !overflow || track.scrollLeft <= 2;
        if (this.next) this.next.disabled = !overflow || track.scrollLeft >= max - 2;
      }
    }
    window.customElements.define('vm-row', VmRow);
  }

  if (!window.customElements.get('vm-tabs')) {
    class VmTabs extends HTMLElement {
      connectedCallback() {
        if (this._vmReady) return;
        this._vmReady = true;
        this.addEventListener('click', (event) => {
          var tab = event.target.closest('[role="tab"]');
          if (tab && tab.closest('vm-tabs') === this) this.select(tab, false);
        });
        this.addEventListener('keydown', (event) => {
          var tab = event.target.closest('[role="tab"]');
          if (!tab || tab.closest('vm-tabs') !== this) return;
          var tabs = this.tabs();
          var index = tabs.indexOf(tab);
          if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') index = 0;
          else if (event.key === 'End') index = tabs.length - 1;
          else return;
          event.preventDefault();
          this.select(tabs[index], true);
        });
      }

      tabs() {
        return Array.prototype.filter.call(this.querySelectorAll('[role="tab"]'), (tab) => tab.closest('vm-tabs') === this && !tab.hidden);
      }

      select(selected, moveFocus) {
        this.tabs().forEach((tab) => {
          var on = tab === selected;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.classList.toggle('is-active', on);
          tab.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (moveFocus) selected.focus();
        this.dispatchEvent(new CustomEvent('vm:tab-change', { bubbles: true, detail: { tab: selected } }));
      }
    }
    window.customElements.define('vm-tabs', VmTabs);
  }
})();

/* Input modality (2026-10-28): <html data-vm-input="pointer|key">. vm-2026.css hides focus frames on controls
   while the last input was a tap/click (e.g. the size-guide link or Filtri after its sheet closes); keyboard users
   keep them. Capture phase, so it runs before any handler moves focus. */
(function () {
  var root = document.documentElement;
  function set(mode) { if (root.getAttribute('data-vm-input') !== mode) root.setAttribute('data-vm-input', mode); }
  set('pointer');
  document.addEventListener('pointerdown', function () { set('pointer'); }, true);
  document.addEventListener('mousedown', function () { set('pointer'); }, true);
  document.addEventListener('touchstart', function () { set('pointer'); }, { capture: true, passive: true });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.key === 'Escape' || e.key === 'Enter' || e.key === ' ' || e.key.indexOf('Arrow') === 0) set('key');
  }, true);
})();
